import { toast } from 'react-toastify';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiChevronUp, FiChevronDown } from 'react-icons/fi';
import { FaSpinner } from 'react-icons/fa';
import Breadcrumb from '../../atoms/Breadcrumb';
import { DraweeDetail } from './batchTypes';
import { getBatchDetail, refreshPaf, reprocessBatch, continueAnalysis, BatchDetail, BatchDetailItem } from '../../../services/CreditAnalysisService';
import DraweeDetailsModal from './DraweeDetailsModal';

const bureaus = [
    { code: 'BLOCKLIST', name: 'Blocklist' },
    { code: 'IBAMA', name: 'Ibama' },
    { code: 'SCR', name: 'SCR' },
    { code: 'CCIR', name: 'CCIR' },
    { code: 'ACRISCORE', name: 'AgriScore', pfOnly: true },
    { code: 'SERASA', name: 'Serasa', pjOnly: true },
    { code: 'QUOD', name: 'Quod', pjOnly: true },
    { code: 'BOAVISTA', name: 'BoaVista', pfOnly: true },
];

const getBureauStatusIcon = (status: string, isInactive?: boolean) => {
    if (isInactive) return <span className="text-gray-300 dark:text-gray-600">-</span>;

    const dotColors: Record<string, string> = {
        approved: 'bg-green-500',
        rejected: 'bg-red-500',
        pending: 'bg-yellow-500',
        processing: 'bg-blue-500',
        error: 'bg-violet-600',
    };

    const color = dotColors[status];
    if (!color) return <span className="text-gray-400">-</span>;
    return <span className={`inline-block w-3 h-3 rounded-full ${color}`} />;
};

export const BatchProcessingDetail: React.FC = () => {
    const { batchId } = useParams<{ batchId: string }>();
    const navigate = useNavigate();
    const [selectedDrawees, setSelectedDrawees] = useState<Set<string>>(new Set());
    const [batch, setBatch] = useState<BatchDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [draweeModalDoc, setDraweeModalDoc] = useState<string | null>(null);
    const [draweeModalItemId, setDraweeModalItemId] = useState<string | null>(null);
    const [isReprocessing, setIsReprocessing] = useState(false);

    // Filters
    const [filterName, setFilterName] = useState('');
    const [filterDoc, setFilterDoc] = useState('');

    // Sort: field + direction
    const [sortField, setSortField] = useState<string>('name');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

    const toggleSort = useCallback((field: string) => {
        setSortField(prev => {
            if (prev === field) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); return field; }
            setSortDir('asc');
            return field;
        });
    }, []);

    useEffect(() => {
        if (!batchId) return;
        let stopped = false;
        const fetchDetail = () => {
            getBatchDetail(batchId)
                .then(data => {
                    setBatch(data);
                    if (data.status === 'COMPLETED' || data.status === 'ERROR') stopped = true;
                })
                .catch(err => toast.error('Erro ao carregar detalhes do lote'))
                .finally(() => setLoading(false));
        };
        fetchDetail();
        const interval = setInterval(() => {
            if (stopped) { clearInterval(interval); return; }
            fetchDetail();
        }, 30000);
        return () => clearInterval(interval);
    }, [batchId]);

    const drawees: DraweeDetail[] = useMemo(() => {
        if (!batch) return [];
        return batch.items.map((item: BatchDetailItem) => ({
            id: item.id,
            name: item.draweeName,
            document: item.document,
            pafDate: item.pafDate,
            errorMessage: item.errorMessage || null,
            bureauResults: bureaus.map(bureau => {
                const apiStatus = (item.bureauStatuses || {})[bureau.code] || '';
                const apiError = (item.bureauErrors || {})[bureau.code] || '';
                const mapped = apiStatus === 'SUCCESS' ? 'approved'
                    : apiStatus === 'REJECTED' ? 'rejected'
                    : apiStatus === 'ERROR' ? 'error'
                    : apiStatus === 'SKIPPED' ? 'not_processed'
                    : apiStatus === 'PROCESSING' ? 'processing'
                    : apiStatus === 'PENDING' ? 'pending'
                    : apiStatus ? 'pending'
                    : 'not_processed';
                return {
                    bureauName: bureau.name,
                    bureauCode: bureau.code,
                    status: mapped as 'approved' | 'rejected' | 'pending' | 'not_processed' | 'error',
                    reason: apiError || undefined,
                };
            }),
        }));
    }, [batch]);

    // Bureau status priority for sorting (worst first)
    const bureauStatusPriority: Record<string, number> = { error: 0, rejected: 1, pending: 2, not_processed: 3, approved: 4 };

    const filteredDrawees = useMemo(() => {
        let result = drawees;
        if (filterName) {
            const lower = filterName.toLowerCase();
            result = result.filter(d => d.name.toLowerCase().includes(lower));
        }
        if (filterDoc) {
            const digits = filterDoc.replace(/\D/g, '');
            result = result.filter(d => d.document.replace(/\D/g, '').includes(digits));
        }

        // Sort
        result = [...result].sort((a, b) => {
            let cmp = 0;
            if (sortField === 'name') {
                cmp = a.name.localeCompare(b.name);
            } else if (sortField === 'document') {
                cmp = a.document.localeCompare(b.document);
            } else if (sortField === 'pafDate') {
                cmp = (a.pafDate || '').localeCompare(b.pafDate || '');
            } else {
                // Sort by specific bureau status
                const brA = a.bureauResults.find(br => br.bureauCode === sortField);
                const brB = b.bureauResults.find(br => br.bureauCode === sortField);
                cmp = (bureauStatusPriority[brA?.status || 'not_processed'] ?? 3) - (bureauStatusPriority[brB?.status || 'not_processed'] ?? 3);
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });

        return result;
    }, [drawees, filterName, filterDoc, sortField, sortDir]);

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedDrawees(new Set(filteredDrawees.map(d => d.id)));
        } else {
            setSelectedDrawees(new Set());
        }
    };

    const handleSelectDrawee = (id: string, checked: boolean) => {
        const newSelected = new Set(selectedDrawees);
        if (checked) {
            newSelected.add(id);
        } else {
            newSelected.delete(id);
        }
        setSelectedDrawees(newSelected);
    };

    const handleContinueAnalysis = async () => {
        if (!batchId || selectedDrawees.size === 0) return;
        try {
            const result = await continueAnalysis(batchId, Array.from(selectedDrawees));
            alert(`Análise continuada: ${result.itemCount} sacado(s). Pipeline: ${result.pipeline.join(', ')}`);
            setSelectedDrawees(new Set());
            // Reload batch detail
            const data = await getBatchDetail(batchId);
            setBatch(data);
        } catch (err: any) {
            alert(`Erro ao continuar análise: ${err.message || err}`);
        }
    };

    if (loading) {
        return <div className="p-4">Carregando...</div>;
    }

    if (!batch) {
        return <div className="p-4">Lote não encontrado</div>;
    }

    const renderRows = (data: DraweeDetail[]) => {
        return data.map((drawee) => {
            const isPJ = drawee.document.replace(/\D/g, '').length === 14;
            const isPF = drawee.document.replace(/\D/g, '').length === 11;

            return (
                <tr key={drawee.id} className="hover:bg-gray-50 dark:hover:bg-slate-700">
                    <td className="px-4 py-3">
                        <input
                            type="checkbox"
                            checked={selectedDrawees.has(drawee.id)}
                            onChange={(e) => handleSelectDrawee(drawee.id, e.target.checked)}
                            className="w-4 h-4"
                        />
                    </td>
                    <td className="px-4 py-3 text-sm">{drawee.name}</td>
                    <td className="px-4 py-3 text-sm font-mono whitespace-nowrap">
                        <button
                            onClick={() => { setDraweeModalDoc(drawee.document); setDraweeModalItemId(drawee.id); }}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
                        >
                            {drawee.document}
                        </button>
                    </td>
                    <td className="px-4 py-3 text-sm">
                        {drawee.pafDate || '-'}
                    </td>
                    {bureaus.map(bureau => {
                        const result = drawee.bureauResults.find(b => b.bureauCode === bureau.code);
                        const isInactive = (bureau.pjOnly && isPF) || (bureau.pfOnly && isPJ);

                        return (
                            <td key={bureau.code} className="px-4 py-3 text-center">
                                <div className="flex items-center justify-center gap-2">
                                    {result && getBureauStatusIcon(result.status, isInactive)}
                                    {result?.reason && !isInactive && (
                                        <span className="text-xs text-gray-500" title={result.reason}>ⓘ</span>
                                    )}
                                </div>
                            </td>
                        );
                    })}
                    <td className="px-4 py-3 text-sm">
                        {(() => {
                            const errors: string[] = [];
                            if (drawee.errorMessage) errors.push(drawee.errorMessage);
                            drawee.bureauResults.forEach(br => {
                                if (br.reason && br.status === 'error') errors.push(`${br.bureauCode}: ${br.reason}`);
                            });
                            if (!errors.length) return <span className="text-gray-400">-</span>;
                            return (
                                <div className="max-w-xs">
                                    {errors.map((e, i) => (
                                        <div key={i} className="text-xs text-red-600 dark:text-red-400 truncate" title={e}>{e}</div>
                                    ))}
                                </div>
                            );
                        })()}
                    </td>
                </tr>
            );
        });
    };

    const SortIcon: React.FC<{ field: string }> = ({ field }) => {
        if (sortField !== field) return <span className="text-gray-300 ml-1">⇅</span>;
        return sortDir === 'asc' ? <FiChevronUp className="inline ml-1" /> : <FiChevronDown className="inline ml-1" />;
    };

    return (
        <div className="h-full flex flex-col bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100">
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-slate-800 z-10 px-4 pt-6 pb-4 border-b border-gray-200 dark:border-slate-600">
                <Breadcrumb items={[
                    { label: 'Processamentos', path: '/credit-analysis/processamentos' },
                    { label: 'Detalhes do Lote' },
                ]} className="mb-2" />

                <div className="flex justify-between items-start">
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-semibold">{batch.batchName}</h1>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                batch.status === 'COMPLETED' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : batch.status === 'ERROR' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            }`}>{batch.status === 'COMPLETED' ? 'Concluído' : batch.status === 'ERROR' ? 'Erro' : 'Processando'}</span>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Número: {batch.batchNumber} | Criado em: {new Date(batch.createdAt).toLocaleString('pt-BR')}
                        </p>
                    </div>

                    <div className="flex gap-2">
                        {drawees.length > 0 && drawees.every(d => d.errorMessage) && (
                            <button
                                disabled={isReprocessing}
                                onClick={async () => {
                                    if (!batchId) return;
                                    setIsReprocessing(true);
                                    try {
                                        await reprocessBatch(batchId);
                                        const data = await getBatchDetail(batchId);
                                        setBatch(data);
                                    } catch (err: any) {
                                        alert(`Erro ao reprocessar: ${err.message || err}`);
                                    } finally {
                                        setIsReprocessing(false);
                                    }
                                }}
                                className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isReprocessing ? <><FaSpinner className="animate-spin" /> Reprocessando...</> : 'Reprocessar Lote'}
                            </button>
                        )}
                        <button
                            onClick={() => navigate(`/credit-analysis/batch/${batch.id}/analysis`)}
                            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                        >
                            Ver Análise
                        </button>

                        {selectedDrawees.size > 0 && (
                            <>
                                <button
                                    onClick={async () => {
                                        if (!batchId) return;
                                        try {
                                            const result = await refreshPaf(batchId, Array.from(selectedDrawees));
                                            const data = await getBatchDetail(batchId);
                                            setBatch(data);
                                            setSelectedDrawees(new Set());
                                            alert(`PAF atualizada para ${result.updated} sacado(s).`);
                                        } catch (err) {
                                            alert('Erro ao atualizar PAF.');
                                            toast.error('Erro ao atualizar PAF');
                                        }
                                    }}
                                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                                >
                                    Atualizar PAF ({selectedDrawees.size})
                                </button>
                                <button
                                    onClick={() => handleContinueAnalysis()}
                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                    Continuar Análise ({selectedDrawees.size})
                                </button>
                                <select
                                    onChange={async (e) => {
                                        const bureau = e.target.value;
                                        if (!bureau || !batchId || selectedDrawees.size === 0) return;
                                        e.target.value = '';
                                        try {
                                            const result = await continueAnalysis(batchId, Array.from(selectedDrawees), bureau);
                                            alert(`Birô ${bureau} disparado para ${result.itemCount} sacado(s).`);
                                            setSelectedDrawees(new Set());
                                            const data = await getBatchDetail(batchId);
                                            setBatch(data);
                                        } catch (err: any) {
                                            alert(`Erro: ${err.message || err}`);
                                        }
                                    }}
                                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                                    defaultValue=""
                                >
                                    <option value="">Rodar em Birô...</option>
                                    {bureaus.map(b => (
                                        <option key={b.code} value={b.code}>{b.name}</option>
                                    ))}
                                </select>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Filtros */}
            <div className="px-4 py-3 flex gap-3 items-center border-b border-gray-200 dark:border-slate-600">
                <input
                    type="text"
                    placeholder="Filtrar por nome..."
                    value={filterName}
                    onChange={e => setFilterName(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-500 rounded bg-white dark:bg-slate-700 w-64"
                />
                <input
                    type="text"
                    placeholder="CPF/CNPJ..."
                    value={filterDoc}
                    onChange={e => setFilterDoc(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-500 rounded bg-white dark:bg-slate-700 w-48"
                />
                <span className="text-sm text-gray-500 ml-auto">{drawees.filter(d => d.bureauResults.some(b => b.status !== 'not_processed')).length} de {drawees.length} sacado(s) avaliado(s)</span>
            </div>

            {/* Tabela */}
            <div className="mx-4 mt-4 border border-gray-200 dark:border-slate-600 rounded">
                <div className="overflow-auto" style={{ height: 'calc(100vh - 260px)' }}>
                    <table className="w-full text-gray-900 dark:text-gray-100">
                        <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0 z-10">
                            <tr>
                                <th className="px-4 py-3 text-left bg-gray-50 dark:bg-slate-700">
                                    <input
                                        type="checkbox"
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                        className="w-4 h-4"
                                    />
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-semibold bg-gray-50 dark:bg-slate-700 cursor-pointer select-none" onClick={() => toggleSort('name')}>
                                    Sacado<SortIcon field="name" />
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-semibold bg-gray-50 dark:bg-slate-700 cursor-pointer select-none" onClick={() => toggleSort('document')}>
                                    CPF/CNPJ<SortIcon field="document" />
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-semibold bg-gray-50 dark:bg-slate-700 cursor-pointer select-none" onClick={() => toggleSort('pafDate')}>
                                    Data da PAF<SortIcon field="pafDate" />
                                </th>
                                {bureaus.map(b => (
                                    <th key={b.code} className="px-4 py-3 text-center text-sm font-semibold bg-gray-50 dark:bg-slate-700 cursor-pointer select-none" onClick={() => toggleSort(b.code)}>
                                        {b.name}<SortIcon field={b.code} />
                                    </th>
                                ))}
                                <th className="px-4 py-3 text-left text-sm font-semibold bg-gray-50 dark:bg-slate-700">Erros</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-600">
                            {renderRows(filteredDrawees)}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Legenda de cores */}
            <div className="mx-4 mt-2 mb-4 flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-green-500" /> Aprovado</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-red-500" /> Rejeitado</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-yellow-500" /> Pendente</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-blue-500" /> Processando</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-violet-600" /> Erro</span>
            </div>


            {draweeModalDoc && (
                <DraweeDetailsModal
                    isOpen={true}
                    onClose={() => { setDraweeModalDoc(null); setDraweeModalItemId(null); }}
                    document={draweeModalDoc}
                    itemId={draweeModalItemId}
                />
            )}
        </div>
    );
};
